import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/ui/Layout';
import Card from '../../components/ui/Card';
import StatusPill from '../../components/ui/StatusPill';
import EmptyState from '../../components/ui/EmptyState';
import LoadingSkeleton from '../../components/ui/LoadingSkeleton';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import { useNavigate as useNav } from 'react-router-dom';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  IconFirstAidKit,
  IconPlus,
  IconSearch,
} from '@tabler/icons-react';

export default function RiskAssessmentList() {
  const { language } = useTranslation();
  const { orgId, userProfile, role } = useAuth();
  const navigate = useNavigate();
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    const load = async () => {
      if (!orgId) return;
      try {
        const snap = await getDocs(query(
          collection(db, 'organisations', orgId, 'riskAssessments'),
          orderBy('assessedAt', 'desc')
        ));
        setAssessments(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    load();
  }, [orgId]);

  const filtered = assessments.filter(a => {
    const q = search.toLowerCase();
    const matchSearch = !q || a.title?.toLowerCase().includes(q) || a.assessedBy?.toLowerCase().includes(q);
    const matchStatus = !filterStatus || a.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const active = assessments.filter(a => a.status === 'active').length;

  const formatDate = (ts) => {
    if (!ts) return '—';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString(language, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const highRisk = (ra) => ra.hazards?.some(h => h.riskRating >= 15);

  return (
    <Layout>
      <div style={{ padding: 'var(--main-padding)' }}>
        <PageHeader
          title="Risk assessments"
          subtitle={`${assessments.length} total · ${active} active`}
          actions={
            <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/risk-assessments/new')}>
              New assessment
            </Button>
          }
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Total', value: assessments.length, colour: 'var(--color-text-primary)' },
            { label: 'Active', value: active, colour: active > 0 ? 'var(--color-success)' : 'var(--color-text-tertiary)' },
            { label: 'Draft', value: assessments.filter(a => a.status === 'draft').length, colour: 'var(--color-warning)' },
          ].map(s => (
            <div key={s.label} style={{ background: 'var(--color-bg-primary)', border: '0.5px solid var(--color-border-light)', borderRadius: 'var(--radius-lg)', padding: '14px 16px' }}>
              <div style={{ fontSize: 28, fontWeight: 400, color: s.colour, marginBottom: 4 }}>{s.value}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{s.label}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <IconSearch size={14} stroke={1.5} color="var(--color-text-tertiary)" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }} />
            <input
              type="text"
              placeholder="Search risk assessments..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ paddingLeft: 32, width: '100%', boxSizing: 'border-box' }}
            />
          </div>
          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            style={{ fontSize: 13, padding: '8px 12px', borderRadius: 'var(--radius-md)', border: '0.5px solid var(--color-border-medium)', background: 'var(--color-bg-primary)', minWidth: 130 }}
          >
            <option value="">All statuses</option>
            <option value="draft">Draft</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        <Card padding="0">
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 0.8fr 60px', padding: '8px 16px', borderBottom: '0.5px solid var(--color-border-light)', background: 'var(--color-bg-secondary)' }}>
            {['Title', 'Assessed by', 'Review date', 'Status', ''].map(h => (
              <div key={h} style={{ fontSize: 10, color: 'var(--color-text-tertiary)', letterSpacing: '0.04em' }}>{h}</div>
            ))}
          </div>
          {loading ? (
            <LoadingSkeleton rows={5} />
          ) : filtered.length === 0 ? (
            <EmptyState
              icon={<IconFirstAidKit size={32} stroke={1.5} />}
              title="No risk assessments found"
              message={search || filterStatus ? 'Try adjusting your filters' : 'Create your first risk assessment'}
              action={!search && !filterStatus && (
                <Button variant="primary" icon={<IconPlus size={14} stroke={1.5} />} onClick={() => navigate('/risk-assessments/new')}>
                  New assessment
                </Button>
              )}
            />
          ) : filtered.map((ra, i) => (
            <div
              key={ra.id}
              onClick={() => navigate('/risk-assessments/' + ra.id)}
              style={{
                display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 0.8fr 60px',
                padding: '12px 16px',
                borderBottom: i < filtered.length - 1 ? '0.5px solid var(--color-border-light)' : 'none',
                cursor: 'pointer', alignItems: 'center',
                background: highRisk(ra) && ra.status === 'active' ? 'var(--color-warning-light)' : 'transparent',
              }}
              onMouseEnter={e => !highRisk(ra) && (e.currentTarget.style.background = 'var(--color-bg-secondary)')}
              onMouseLeave={e => !highRisk(ra) && (e.currentTarget.style.background = 'transparent')}
            >
              <div>
                <div style={{ fontSize: 13, color: 'var(--color-text-primary)', marginBottom: 2 }}>{ra.title || 'Untitled'}</div>
                <div style={{ fontSize: 11, color: 'var(--color-text-tertiary)', textTransform: 'capitalize' }}>{ra.type} · {formatDate(ra.assessedAt)}</div>
              </div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{ra.assessedBy || '—'}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{ra.reviewDate || '—'}</div>
              <StatusPill status={ra.status || 'draft'} />
              <div style={{ fontSize: 12, color: 'var(--color-brand)', textAlign: 'right' }}>View</div>
            </div>
          ))}
        </Card>
      </div>
    </Layout>
  );
}